import * as _ from 'lodash';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Denomination } from '~models/denomination.model';
import { ETagProduct, ETagProductNameMapping, ETagType, GiftCard } from '~models/gift-card.model';
import { environment } from '../../../environments/environment';
import { constants } from '../../app.constants';
import { isRenderingForMobileApp } from 'src/constants';
import { Currency } from '~models/currency.model';
import { Utils } from 'src/app/utils/utilities';
import { SelectOptionService } from 'src/app/shared-data/select-option.service';
import { CheckoutStateService } from 'src/app/shared-data/checkout-state.service';
import { ZipYiService, TrxService, DEFAULT_CROSS_DOMAIN_CSS_CLASS } from '~services';
import { Vendor } from '~models';
import { OAuthService } from 'angular-oauth2-oidc';
import { WebModalService } from '@zip/ng-styleguide';
import { Subject } from 'rxjs';
import { Location } from '@angular/common';
import { ZipYiContext } from '~services/biocatch/zip-yi-context.enum';
import { ZipYiElement } from '~services/biocatch/zip-yi-element.enum';

@Component({
  selector: 'app-new-design-variant',
  templateUrl: './new-design-variant.component.html',
  styleUrls: ['./new-design-variant.component.scss'],
})
export class NewDesignVariantComponent implements OnInit, OnDestroy {
  titleSelectOption: string;
  buyTypeConst: any;
  giftingEnabled: boolean;
  numTruncate: number;
  isLongDesc: boolean;
  isSeeMore: boolean;
  importantContentOrigin: string;
  importantContentTruncate: string;

  @Input()
  public customDenomination: Denomination = {
    isCustom: true,
    currency: Currency.AUD,
    price: 0,
    stock: 'IN_STOCK',
  };

  @Input()
  customDenominationPrice = 0;

  @Input()
  isCaltexCard = false;

  @Input()
  public isMobileRender = isRenderingForMobileApp;

  @Output()
  public readonly checkoutForYourself = new EventEmitter<void>();

  @Output()
  public readonly checkoutForFriend = new EventEmitter<void>();

  @Output()
  public readonly back = new EventEmitter<void>();

  @Input()
  public readonly giftCard: GiftCard;

  @Input()
  public selectedDenomination: Denomination;

  @Output()
  public readonly selectedDenominationChange = new EventEmitter<Denomination>();

  @Input()
  public readonly animate = true;

  public readonly appPrefix = environment.appPrefix;

  public showDisclaimer = false;

  @Input()
  public importantContent: string;

  @Input()
  public showBackButton = false;
  public showDelivery = false;

  @Input()
  public browseCards: GiftCard[] = [];

  @Output()
  public readonly browseClick = new EventEmitter<GiftCard>();

  @Output()
  public readonly continueShopping = new EventEmitter<void>();

  @Input()
  public isAmazonGiftCard = false;

  @Input()
  public isBigWGiftCard = false;

  @Input()
  public showGiftCardSuffix = false;

  @Input()
  public cardAvailability: boolean;

  @Input()
  public buyType;

  @Output()
  public readonly buyTypeChange = new EventEmitter<string>();

  public showProductFeatures = false;

  cashbackPromoTag = '';
  plainPromoTag = '';
  showCashBackDisclaimer = false;
  cashbackPercentage = '';
  giftCardDisplayName = '';
  SEOScriptId = '';
  buyForFriendLabel = 'Buy for a friend';
  buyForMyselfLabel = 'Buy for myself';
  crossDomainCssClass = DEFAULT_CROSS_DOMAIN_CSS_CLASS;
  zipYiElement = ZipYiElement;

  private destroy$ = new Subject<boolean>();

  constructor(
    public selectOptionService: SelectOptionService,
    public checkoutStateService: CheckoutStateService,
    private oauthService: OAuthService,
    private webModalService: WebModalService,
    private location: Location,
    private zipYiService: ZipYiService,
    private trxService: TrxService,
  ) {
    this.titleSelectOption = 'Value';
    this.buyTypeConst = constants.BUY_TYPES;
    this.giftingEnabled = true;
    this.numTruncate = 224;
    this.importantContentOrigin = '';
    this.importantContentTruncate = '';
    this.isLongDesc = false;
    this.isSeeMore = false;
  }

  async ngOnInit() {
    this.selectedDenominationChange.emit(this.selectedDenomination);

    this.handleImportantContent();
    this.checkoutStateService.setStateCheckoutBtnTriggered(false);
    this.giftCardDisplayName = this.getGiftCardDisplayName();
    this.cashbackPromoTag = this.getGiftCardPromoTag();
    this.plainPromoTag = this.getGiftCardPlainTag();
    this.showCashBackDisclaimer = this.getShouldShowCashbackDisclaimer();
    const structuredProductDataForSEO = JSON.stringify(this.constructStructuredProductData());
    this.SEOScriptId = `${this.giftCard.id}_SEO_Script`;
    Utils.setScriptInHead(this.SEOScriptId, 'application/ld+json', structuredProductDataForSEO);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    Utils.removeScriptInHeadById(this.SEOScriptId);
  }

  checkAppVersion(): boolean {
    const agent = navigator.userAgent.toLowerCase(),
      start = agent.indexOf('/'),
      end = agent.indexOf(' ', start),
      appInfo = agent.substr(0, end).split('/');
    if (!appInfo[0].includes('zip')) {
      return false;
    }

    const compareVersion = (requiredVersion: number[]) => {
      const currentVersion = appInfo[1].split('.').map(Number);
      for (let i = 0; ; i++) {
        if (i >= requiredVersion.length) {
          return true;
        }
        if (i >= currentVersion.length) {
          return false;
        }
        const diff = currentVersion[i] - requiredVersion[i];
        if (diff) {
          return diff > 0 ? true : false;
        }
      }
    };

    if (/android/.test(agent) || (/iphone|ipod|ipad/.test(agent) && start > -1)) {
      return compareVersion([1, 2, 8]);
    } else {
      return false;
    }
  }

  changeCustomDenominationPrice(price: number) {
    this.customDenominationPrice = price;
    this.selectedDenominationChange.emit({
      ...this.selectedDenomination,
      price: this.customDenominationPrice,
    });
  }

  changeDenomination(denomination: Denomination) {
    this.selectedDenomination = denomination;
    this.selectedDenominationChange.emit(this.selectedDenomination);
  }

  async doCheckout() {
    await this.trxService.sendUIClickEvent({
      control_type: 'button',
      label: this.buyForMyselfLabel,
      action_type: 'click',
      control_id: null,
      destination_target_url: null,
    });

    if (!this.oauthService.hasValidAccessToken()) {
      return this.redirectToLogin();
    }

    if (!Utils.checkValid(this.selectOptionService.dataSelected)) {
      this.selectOptionService.isValidData = false;

      return;
    }

    return this.checkoutForYourself.emit();
  }

  doCheckoutForFriend() {
    if (!this.oauthService.hasValidAccessToken()) {
      return this.redirectToLogin();
    }

    return this.checkoutForFriend.emit();
  }

  async chooseBuyType(type: string) {
    await this.trxService.sendUIClickEvent({
      control_type: 'button',
      label: type === this.buyTypeConst.FRIEND ? this.buyForFriendLabel : this.buyForMyselfLabel,
      action_type: 'click',
      control_id: null,
      destination_target_url: null,
    });

    if (!this.oauthService.hasValidAccessToken() && type !== '') {
      return this.redirectToLogin();
    }

    this.buyType = type;
    this.buyTypeChange.emit(this.buyType);
  }

  doTruncate() {
    let dataTemp = '';

    dataTemp = this.importantContent;
    if (dataTemp.length > this.numTruncate) {
      return dataTemp.slice(0, this.numTruncate) + ' ... ';
    }

    return '';
  }

  private getGiftCardPlainTag(): string {
    const plainPromo = this.giftCard.tags?.find((t) => t.type === ETagType.PLAIN);
    if (!plainPromo) return '';

    return plainPromo.text;
  }

  private getGiftCardPromoTag(): string {
    const cashbackPromo = this.giftCard.tags?.find((t) => t.type === ETagType.CASHBACK);
    if (!cashbackPromo) {
      return '';
    }

    const promoProductInfo =
      cashbackPromo.products?.length === 1
        ? ` with ${ETagProductNameMapping[cashbackPromo.products[0]]}`
        : '';

    // If cashback percentage is hidden, show a static text
    const isLockedState = cashbackPromo.text === 'Cashback deal';

    if (isLockedState) {
      return `Cashback ${promoProductInfo}`;
    }

    return `${cashbackPromo.text}${promoProductInfo}`;
  }

  handleImportantContent() {
    if (this.importantContent) {
      this.importantContentOrigin = this.importantContent;

      if (this.importantContent.length > this.numTruncate) {
        this.isLongDesc = true;
        this.isSeeMore = false;

        this.importantContentTruncate = this.doTruncate();
      } else {
        this.importantContentTruncate = this.importantContentOrigin;
      }
    }
  }

  private redirectToLogin() {
    this.oauthService.initLoginFlow(this.location.path());
  }

  getShouldShowCashbackDisclaimer() {
    const cashbackPromo = this.giftCard.tags?.find((t) => t.type === ETagType.CASHBACK);
    if (cashbackPromo) {
      this.cashbackPercentage = cashbackPromo.text?.match(/(\d*(\.\d+)?)%/g)?.[0];
    }

    return (
      cashbackPromo?.products?.length === 1 &&
      cashbackPromo?.products[0] === ETagProduct.ZM &&
      cashbackPromo.text !== 'Cashback deal'
    );
  }

  downloadApp() {
    window.location.href = 'https://zip.co/au/app';
  }

  showMore() {
    this.importantContentTruncate = this.importantContentOrigin;
    this.isSeeMore = true;
  }

  showLess() {
    this.handleImportantContent();
  }

  public get isGifEnabled() {
    return this.giftCard.vendor !== Vendor.Eonx;
  }

  private getGiftCardDisplayName() {
    if (this.isAmazonGiftCard) {
      return 'Amazon.com.au eGift Card';
    }

    return this.showGiftCardSuffix ? `${this.giftCard.brand} Gift Card` : this.giftCard.brand;
  }

  private async showDualAccountHolderDialog(checkoutAction: EventEmitter<void>) {
    this.zipYiService.setContext(ZipYiContext.ZipMoneyOnlyCashbackModal);
    const yes = await this.webModalService.open({
      title: 'Checkout with Zip Money to get cashback',
      message: 'Select Zip Money as your payment method in the next screen to receive cashback.',
      actionLeft: '',
      actionRight: 'Continue',
      icon: 'megaphone',
      iconType: 'fearless-icons',
    });

    if (yes) {
      checkoutAction.emit();
    }

    const cancel = yes === undefined;
    if (cancel) {
      this.zipYiService.setContext(ZipYiContext.CheckoutPage);
    }
  }

  private async showPromoOnlyForZipMoneyAccountHolderDialog(checkoutAction: EventEmitter<void>) {
    this.zipYiService.setContext(ZipYiContext.ApplyForZipMoneyModal);
    const yes = await this.webModalService.open({
      title: 'Apply for Zip Money to get cashback',
      message: 'Cashback on gift cards is only available with Zip Money.',
      actionLeft: 'Apply for Zip Money',
      actionRight: 'Continue without cashback',
      icon: 'megaphone',
      iconType: 'fearless-icons',
    });
    if (yes) {
      return checkoutAction.emit();
    }

    const applyForZipMoney = yes === false;
    if (applyForZipMoney) {
      const zipMoneyUrl = isRenderingForMobileApp
        ? 'co.zip.sandbox://deeplink/browser/external-browser/aHR0cHM6Ly96aXAuY28vYXUvemlwLW1vbmV5LW5hdGl2ZQ'
        : 'https://zip.co/au/zip-money-native';

      if (isRenderingForMobileApp) {
        window.location.href = zipMoneyUrl;
      } else {
        window.open(zipMoneyUrl, '_blank');
      }
    }

    const cancel = yes === undefined;
    if (cancel) {
      this.zipYiService.setContext(ZipYiContext.CheckoutPage);
    }
  }

  private constructStructuredProductData() {
    const sortedDenominations = _.sortBy(this.giftCard.denominations, [(o) => o.price]);
    return {
      '@context': 'https://schema.org/',
      '@type': 'Product',
      name: this.giftCardDisplayName,
      image: [this.giftCard.image],
      description: `Buy ${this.giftCard.brand} gift cards online with Zip and receive them instantly via email. Simply load up your ${this.giftCard.brand} gift vouchers to spend online or in-store.`,
      sku: this.giftCard.brand,
      brand: { '@type': 'Brand', name: this.giftCard.brand },
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: sortedDenominations[0].price,
        highPrice: sortedDenominations[sortedDenominations.length - 1].price,
        priceCurrency: sortedDenominations[0].currency,
        availability: 'https://schema.org/InStock',
        itemCondition: 'https://schema.org/NewCondition',
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: {
            '@type': 'MonetaryAmount',
            value: '0',
            currency: sortedDenominations[0].currency,
          },
        },
      },
    };
  }
}
